import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useDialog } from "@/components/Dialogs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Lock, Users } from "lucide-react";
import { ContentReactions, ReactedBy, ReportLink, Comments, NoteBody, errText, when } from "@/components/ContentBits";

const BLANK = { title: "", body: "", visibility: "private", comments_off: false, show_id: "", show_name: "" };
const NOTE_VIS = { private: "Draft, only me", friends: "My friends" };

function NoteEditor({ initial, ShowPicker, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { title: initial.title, body: initial.body, visibility: initial.visibility, comments_off: !!initial.comments_off, show_id: initial.show_id || "", show_name: initial.show_name || "" } : BLANK);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { title: f.title, body: f.body, visibility: f.visibility, comments_off: f.comments_off, show_id: f.show_id || null };
    try {
      const r = initial ? await api.put(`/notes/${initial.id}`, body) : await api.post("/notes", body);
      toast.success(f.visibility === "friends" ? "Note shared with your friends" : "Draft saved");
      onSaved(r.data);
    } catch (err) { toast.error(errText(err, "Could not save that. Please try again.")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Note">
      <form onSubmit={save} className="flex max-h-[92vh] w-full max-w-xl flex-col rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold">{initial ? "Edit note" : "New note"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 flex-1 space-y-4 overflow-y-auto pr-1">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="note-title">Title</label>
            <input id="note-title" data-testid="note-title" maxLength={100} value={f.title} onChange={(e) => set({ title: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="note-body">Your note</label>
            <textarea id="note-body" data-testid="note-body" rows={10} maxLength={5000} value={f.body} onChange={(e) => set({ body: e.target.value })} placeholder="What's on your mind? A review, a memory, a rant, a recommendation…" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm leading-relaxed" />
            <p className="mt-1 text-right text-xs text-muted-foreground">{f.body.length} / 5000 · plain text, links won't be clickable</p>
          </div>
          <div className="text-sm">
            <span className="mr-2 font-medium">About a show?</span>
            {f.show_id
              ? <span>{f.show_name} <button type="button" className="ml-1 underline" onClick={() => set({ show_id: "", show_name: "" })}>Remove</button></span>
              : <button type="button" data-testid="note-pick-show" className="text-[#C89B3C] underline" onClick={() => setPicking(true)}>Link a show (optional)</button>}
          </div>
          <fieldset>
            <legend className="mb-1 text-sm font-medium">Who can read this?</legend>
            <div className="space-y-1.5 text-sm">
              {Object.entries(NOTE_VIS).map(([v, label]) => (
                <label key={v} className="flex items-center gap-2"><input type="radio" name="note-vis" data-testid={`note-vis-${v}`} checked={f.visibility === v} onChange={() => set({ visibility: v })} /> {label}</label>
              ))}
            </div>
            {f.visibility === "friends" && (
              <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="note-comments-on" checked={!f.comments_off} onChange={(e) => set({ comments_off: !e.target.checked })} /> Let my friends comment</label>
            )}
            <p className="mt-2 text-xs text-muted-foreground">Notes are never public. Only people you've accepted as friends can read them, and you can switch a note back to a draft at any time.</p>
          </fieldset>
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm hover:bg-secondary">Cancel</button>
          <button type="submit" data-testid="note-save" disabled={busy} className="rounded-full bg-[#C89B3C] px-6 py-2 text-sm font-semibold text-black disabled:opacity-60">{busy ? "Saving…" : f.visibility === "friends" ? "Share with friends" : "Save draft"}</button>
        </div>
      </form>
      {picking && <ShowPicker onPick={(s) => { setPicking(false); if (!s.custom) set({ show_id: s.id, show_name: s.name }); else toast.error("Notes can only link to shows on the site."); }} onClose={() => setPicking(false)} />}
    </div>
  );
}

export function NoteCard({ note, mine, canReact = true, canReport = true, canComment = true, onEdit, onDelete, onChange }) {
  return (
    <article className="rounded-2xl border border-border bg-card p-5" data-testid="note-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-serif text-xl font-semibold leading-tight">{note.title}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {mine
              ? <span className="inline-flex items-center gap-1">{note.visibility === "friends" ? <Users className="h-3 w-3" /> : <Lock className="h-3 w-3" />} {NOTE_VIS[note.visibility]}</span>
              : <>by <Link to={`/u/${note.username}`} className="underline">{note.username}</Link></>} · {when(note.updated_at)}
            {mine && note.hidden && <span className="ml-2 rounded bg-red-500/20 px-1.5 py-0.5 text-red-300" data-testid="hidden-flag">Hidden by us</span>}
          </p>
        </div>
        {mine && (
          <div className="flex flex-shrink-0 items-center gap-3 text-sm">
            <button type="button" onClick={() => onEdit(note)} data-testid="note-edit" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            <button type="button" onClick={() => onDelete(note)} data-testid="note-delete" className="inline-flex items-center gap-1 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </div>
        )}
      </div>
      <NoteBody note={note} />
      {mine
        ? (note.visibility === "friends" && <><ContentReactions kind="note" item={note} canReact={false} onChange={onChange} /><ReactedBy item={note} onRemoved={(rid) => onChange({ ...note, reacted_by: note.reacted_by.filter((r) => r.id !== rid) })} /></>)
        : <><ContentReactions kind="note" item={note} canReact={canReact} onChange={onChange} />{canReport && <div className="mt-3"><ReportLink kind="note" id={note.id} /></div>}</>}
      {canComment && note.visibility !== "private" && <Comments kind="note" id={note.id} count={note.comment_count || 0} onCount={(c) => c !== (note.comment_count || 0) && onChange({ ...note, comment_count: c })} />}
    </article>
  );
}

export default function NotesTab({ ShowPicker }) {
  const dialog = useDialog();
  const [notes, setNotes] = useState(null);
  const [friends, setFriends] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/notes").then((r) => setNotes(r.data)).catch(() => setNotes([]));
    api.get("/friends/notes").then((r) => setFriends(r.data)).catch(() => setFriends([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (n) => {
    if (!(await dialog.confirm({ title: "Delete this note?", body: `"${n.title}" will be deleted for good, along with any comments on it.`, confirmLabel: "Delete", danger: true }))) return;
    try { await api.delete(`/content/note/${n.id}`); toast.success("Note deleted"); load(); }
    catch { toast.error("Could not delete that. Please try again."); }
  };
  const patch = (setter) => (u) => setter((a) => a.map((x) => (x.id === u.id ? { ...x, ...u } : x)));

  return (
    <div className="space-y-8" data-testid="notes-tab">
      {dialog.node}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Theatre notes</h2>
          <p className="text-sm text-muted-foreground">Longer thoughts, shared only with your friends. Keep them as drafts until you're ready.</p>
        </div>
        <button type="button" data-testid="new-note" onClick={() => setEditing({ note: null })} className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-5 py-2.5 text-sm font-semibold text-black"><Plus className="h-4 w-4" /> New note</button>
      </div>

      {!notes ? <p className="text-muted-foreground">Loading…</p> : notes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="notes-empty">
          <Pencil className="mx-auto h-8 w-8 text-[#C89B3C]" />
          <p className="mt-3 font-serif text-xl font-semibold">Write your first note</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">A review that doesn't fit in a diary entry, a night you want to remember, or a recommendation for your friends.</p>
        </div>
      ) : (
        <div className="space-y-5">{notes.map((n) => <NoteCard key={n.id} note={n} mine onEdit={(x) => setEditing({ note: x })} onDelete={remove} onChange={patch(setNotes)} />)}</div>
      )}

      {friends.length > 0 && (
        <section data-testid="friends-notes">
          <h2 className="font-serif text-2xl font-semibold">From your friends</h2>
          <div className="mt-4 space-y-5">{friends.map((n) => <NoteCard key={n.id} note={n} onChange={patch(setFriends)} />)}</div>
        </section>
      )}

      {editing && <NoteEditor initial={editing.note} ShowPicker={ShowPicker} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}